import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Api from '../api';
import { useAuthContext } from '../context';

const POLL_INTERVAL_MS = 2000;

export type TelegramBotLoginPhase =
  | 'idle'
  | 'starting'
  | 'waiting'
  | 'approved'
  | 'rejected'
  | 'expired'
  // Login-only attempt for a Telegram account that hasn't signed up yet.
  | 'no_account'
  | 'error';

/** No session is minted until the server sees the browser's own requestId, so a leaked link can't hijack it. */
export const useTelegramBotLogin = (accountType?: 'STAFF' | 'CANDIDATE', mode?: 'login' | 'signup') => {
  const queryClient = useQueryClient();
  const {
    methods: { setTokens },
  } = useAuthContext();

  const [phase, setPhase] = useState<TelegramBotLoginPhase>('idle');
  const [deepLink, setDeepLink] = useState<string | null>(null);

  const requestIdRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  // Guards against a late poll resolving after unmount / reset / a new run.
  const stoppedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  };

  const stop = useCallback(() => {
    stoppedRef.current = true;
    clearTimer();
  }, []);

  const poll = useCallback(async () => {
    const requestId = requestIdRef.current;
    if (!requestId || stoppedRef.current) return;

    try {
      const { data } = await Api.TelegramBotLoginPoll(requestId);
      if (stoppedRef.current) return;

      if (data.status === 'pending') {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }
      if (data.status === 'approved' && data.session) {
        stop();
        queryClient.removeQueries({ queryKey: ['auth', 'profile'] });
        setTokens({
          accessToken: data.session.accessToken,
          refreshToken: data.session.refreshToken,
        });
        setPhase('approved');
        return;
      }
      // rejected | no_account | expired | approved-without-session (treat as expired).
      stop();
      setPhase(
        data.status === 'rejected' ? 'rejected' : data.status === 'no_account' ? 'no_account' : 'expired',
      );
    } catch {
      stop();
      setPhase('error');
    }
  }, [setTokens, queryClient, stop]);

  const start = useCallback(async () => {
    stop();
    stoppedRef.current = false;
    requestIdRef.current = null;
    setDeepLink(null);
    setPhase('starting');

    try {
      const { data } = await Api.TelegramBotLoginStart(accountType, mode);
      if (stoppedRef.current) return;
      requestIdRef.current = data.requestId;
      setDeepLink(data.deepLink);
      setPhase('waiting');
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch {
      setPhase('error');
    }
  }, [accountType, mode, poll, stop]);

  const reset = useCallback(() => {
    stop();
    stoppedRef.current = false;
    requestIdRef.current = null;
    setDeepLink(null);
    setPhase('idle');
  }, [stop]);

  // Stop polling if the component unmounts mid-flight.
  useEffect(() => () => stop(), [stop]);

  return { phase, deepLink, start, reset };
};
